import { Link } from 'react-router-dom'
import { useGlobalMetrics } from '../hooks/useGlobalMetrics'
import { useGlobalActivity } from '../hooks/useGlobalActivity'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts'

const COLORS = ['#8AD0D6', '#5BA3A9', '#3D7A80', '#2A5A5F', '#A8DFE4', '#6EC4CB']

const PRICING = {
  demo: { platform: 0, per_client: 0 },
  design_partner: { platform: 350, per_client: 100 },
  solo: { platform: 750, per_client: 150 },
  advisory_firm: { platform: 1500, per_client: 125 },
  enterprise: { platform: 0, per_client: 0 }
}

const PLAN_LABELS = {
  demo: 'Demo',
  design_partner: 'Design Partner',
  solo: 'Solo',
  advisory_firm: 'Advisory Firm',
  enterprise: 'Enterprise'
}

function calcMRR(planTier, activeClients, settings) {
  const override = settings?.pricing_override
  if (override?.enabled) {
    const platform = override.platform_fee ?? 0
    const perClient = override.per_client ?? 0
    return platform + (perClient * activeClients)
  }
  const pricing = PRICING[planTier] || PRICING.design_partner
  return pricing.platform + (pricing.per_client * activeClients)
}

const ACTION_LABELS = {
  'client.created': 'Created client',
  'client.updated': 'Updated client',
  'assessment.started': 'Started assessment',
  'assessment.completed': 'Completed assessment',
  'user.invited': 'Invited user',
  'user.deleted': 'Deleted user',
  'policy.generated': 'Generated policy',
  'login.success': 'Logged in',
  'login.failed': 'Failed login',
  'mfa.enrolled': 'Enrolled MFA',
  'org.settings_updated': 'Updated org settings'
}

function GlobalDashboard() {
  const { metrics, loading, error } = useGlobalMetrics()
  const { activities: recentActivities } = useGlobalActivity({ limit: 10 })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-12 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    )
  }

  const summary = metrics?.summary || {}
  const orgList = metrics?.orgs || []

  // Calculate total platform MRR
  const totalMRR = orgList.reduce((sum, org) => {
    return sum + calcMRR(org.plan_tier, org.active_clients, org.settings)
  }, 0)

  // Status distribution for pie chart
  const statusDistribution = ['active', 'trial', 'suspended', 'cancelled']
    .map(status => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: orgList.filter(o => o.status === status).length
    }))
    .filter(d => d.value > 0)

  // Client count per org for bar chart
  const clientsByOrg = orgList
    .filter(o => o.active_clients > 0)
    .sort((a, b) => b.active_clients - a.active_clients)
    .slice(0, 10)
    .map(o => ({
      name: o.org_name.length > 20 ? o.org_name.substring(0, 20) + '...' : o.org_name,
      clients: o.active_clients,
      cap: o.client_cap
    }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Global Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Platform overview across all organizations</p>
        </div>
        <Link to="/global/organizations" className="btn-primary">
          Manage Organizations
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Active Organizations</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{summary.active_orgs || 0}</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{summary.total_orgs || 0} total</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Clients</div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{summary.total_clients || 0}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Assessments</div>
          <div className="text-3xl font-bold text-primary-600">{summary.total_assessments || 0}</div>
          <div className="text-xs mt-1">
            <span className="text-amber-500 dark:text-amber-400">{(summary.total_assessments || 0) - (summary.completed_assessments || 0)} In Progress</span>
            <span className="text-gray-300 dark:text-gray-600 mx-1">/</span>
            <span className="text-emerald-500 dark:text-emerald-400">{summary.completed_assessments || 0} Completed</span>
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Platform Users</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{summary.total_users || 0}</div>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Estimate</h3>
          <div className="text-xs text-gray-400 dark:text-gray-500">
            Design Partner: $350 + $100/client | Solo: $750 + $150/client | Advisory Firm: $1,500 + $125/client
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Platform MRR</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              ${totalMRR.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Projected ARR</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              ${(totalMRR * 12).toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Avg Revenue/Org</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              ${orgList.length ? Math.round(totalMRR / orgList.length).toLocaleString() : 0}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Clients per Org */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Clients by Organization</h3>
          {clientsByOrg.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={clientsByOrg} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(63, 90, 106, 0.2)" />
                <XAxis type="number" tick={{ fill: '#7E8F9C', fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={150} tick={{ fill: '#7E8F9C', fontSize: 11 }} />
                <Tooltip
                  formatter={(value) => [value, 'Clients']}
                  contentStyle={{ backgroundColor: '#012438', border: '1px solid #3F5A6A', borderRadius: '8px', color: '#BCC5CD' }}
                  itemStyle={{ color: '#8AD0D6' }}
                  labelStyle={{ color: '#BCC5CD' }}
                />
                <Bar dataKey="clients" fill="#8AD0D6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500 dark:text-gray-400">
              No client data yet
            </div>
          )}
        </div>

        {/* Org Status Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Organization Status</h3>
          {statusDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8AD0D6"
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#012438', border: '1px solid #3F5A6A', borderRadius: '8px', color: '#BCC5CD' }}
                  itemStyle={{ color: '#8AD0D6' }}
                />
                <Legend wrapperStyle={{ color: '#7E8F9C' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500 dark:text-gray-400">
              No organizations yet
            </div>
          )}
        </div>
      </div>

      {/* Organization Health Table */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Organization Health</h3>
          <Link
            to="/global/organizations"
            className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium"
          >
            View All
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Clients</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assessments</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">MRR</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {orgList.map(org => (
                <tr key={org.org_id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => window.location.href = `/global/organizations/${org.org_id}`}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{org.org_name}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      org.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                      : org.status === 'trial' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                      : org.status === 'suspended' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                    }`}>
                      {org.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {PLAN_LABELS[org.plan_tier] || org.plan_tier}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{org.active_clients}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">/ {org.client_cap}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">{org.total_assessments}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({org.completed_assessments} done)</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">{org.active_users}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      ${calcMRR(org.plan_tier, org.active_clients, org.settings).toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
              {orgList.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No organizations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <Link
            to="/global/activity"
            className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium"
          >
            View All
          </Link>
        </div>
        <div className="space-y-3">
          {recentActivities.length > 0 ? recentActivities.map(activity => (
            <div key={activity.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium text-xs">
                    {activity.user_name?.[0] || activity.user_email?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">
                    <span className="font-medium">{activity.user_name || activity.user_email || 'Unknown'}</span>
                    {' '}
                    <span className="text-gray-600 dark:text-gray-400">{ACTION_LABELS[activity.action] || activity.action}</span>
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {activity.org_name || ''} {activity.org_name ? '·' : ''} {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No activity recorded yet</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default GlobalDashboard
